import React, { useEffect, type JSX } from "react";
// MODIFICATION: Import Redux hooks and the actions from your chat slice
import { setDataVersion, startNewProject } from "./ChatSlice_Store"; // Adjust path if necessary
// Import typed Redux hooks
import { useAppDispatch, useAppSelector } from "./hooks/reduxHooks";
import { useAuthIntegration } from "./hooks/useAuthIntegration";
import { selectCurrentProject } from "./ProjectSlice_Store";
import {
  AboutDealOsButton,
  NewChatButton,
  SidebarToggleButton,
} from "./components/header/HeaderControls";
import HeaderModals from "./components/header/HeaderModals";
import HeaderUserMenu from "./components/header/HeaderUserMenu";
import ProjectNameBar from "./components/header/ProjectNameBar";
import { useHeaderMenus, useProjectNameKey } from "./components/header/useHeaderMenus";

interface HeaderComponentProps {
  onToggleSidebar: () => void;
  isMobileSidebarOpen: boolean;
  onOpenWelcomeModal?: () => void;
}

export default function Header_Component({
  onToggleSidebar,
  isMobileSidebarOpen,
  onOpenWelcomeModal,
}: HeaderComponentProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { userName, userEmail, isAuthenticated } = useAppSelector(
    (state) => state.userAuthAndAdminSlice
  );
  const currentProject = useAppSelector(selectCurrentProject);

  const menus = useHeaderMenus(currentProject);
  const projectNameKey = useProjectNameKey(currentProject?.name);

  // Initialize auth integration
  useAuthIntegration();

  // Ensure data version is always set to 2 (Public)
  useEffect(() => {
    dispatch(setDataVersion(2));
  }, [dispatch]);

  const newProject = (): void => void dispatch(startNewProject());

  const nameBar = {
    project: currentProject,
    animationKey: projectNameKey,
    isMenuOpen: menus.isHeaderMenuOpen,
    onOpenSummary: menus.openProjectModal,
    onToggleMenu: menus.toggleHeaderMenu,
  };

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="relative flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
        {/* Left section - Menu button, New button, and Project Name */}
        <div className="flex items-center">
          <SidebarToggleButton
            isMobileSidebarOpen={isMobileSidebarOpen}
            onToggleSidebar={onToggleSidebar}
          />
          <NewChatButton onClick={newProject} />
          <ProjectNameBar variant="desktop" {...nameBar} />
        </div>

        {/* Tablet: absolutely centered project name — independent of section widths */}
        <ProjectNameBar variant="tablet" {...nameBar} />

        {/* Right section - About DealOS + User Info */}
        <div className="flex items-center gap-3 sm:gap-4">
          {onOpenWelcomeModal && <AboutDealOsButton onClick={onOpenWelcomeModal} />}
          {isAuthenticated && <HeaderUserMenu userName={userName} userEmail={userEmail} />}
        </div>
      </div>

      {/* Mobile: full-width project name bar */}
      <ProjectNameBar variant="mobile" {...nameBar} />

      <HeaderModals project={currentProject} menus={menus} onDeleted={newProject} />
    </header>
  );
}
